import { useState } from 'react'
import { FileText, User, Building2, Settings, AlertTriangle, Eye, Clock, Globe } from 'lucide-react'

interface AuditLog {
  id: string
  actor_id: string
  actor_email: string
  action: string
  target_type: string
  target_id: string | null
  target_identifier: string | null
  description: string | null
  old_values: Record<string, unknown> | null
  new_values: Record<string, unknown> | null
  ip_address: string | null
  user_agent: string | null
  created_at: string
}

interface AuditLogTableProps {
  logs: AuditLog[]
  loading?: boolean
}

export function AuditLogTable({ logs, loading = false }: AuditLogTableProps) {
  const [expandedLog, setExpandedLog] = useState<string | null>(null)

  const formatDateTime = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    })
  }

  const getActionBadge = (action: string) => {
    const actionMap: Record<string, { label: string; color: string }> = {
      'organization.created': { label: 'Org Criada', color: 'bg-green-100 text-green-700' },
      'organization.updated': { label: 'Org Atualizada', color: 'bg-blue-100 text-blue-700' },
      'organization.deleted': { label: 'Org Excluída', color: 'bg-red-100 text-red-700' },
      'subscription.created': { label: 'Assinatura Criada', color: 'bg-green-100 text-green-700' },
      'subscription.updated': { label: 'Assinatura Atualizada', color: 'bg-blue-100 text-blue-700' },
      'subscription.cancelled': { label: 'Assinatura Cancelada', color: 'bg-red-100 text-red-700' },
      'user.blocked': { label: 'Usuário Bloqueado', color: 'bg-red-100 text-red-700' },
      'user.unblocked': { label: 'Usuário Desbloqueado', color: 'bg-green-100 text-green-700' },
      'user.impersonated': { label: 'Impersonação', color: 'bg-amber-100 text-amber-700' },
      'plan.created': { label: 'Plano Criado', color: 'bg-green-100 text-green-700' },
      'plan.updated': { label: 'Plano Atualizado', color: 'bg-blue-100 text-blue-700' },
      'plan.deleted': { label: 'Plano Excluído', color: 'bg-red-100 text-red-700' },
      'alert.created': { label: 'Alerta Criado', color: 'bg-amber-100 text-amber-700' },
      'alert.resolved': { label: 'Alerta Resolvido', color: 'bg-green-100 text-green-700' },
      'config.updated': { label: 'Config Atualizada', color: 'bg-purple-100 text-purple-700' },
      'payment.refunded': { label: 'Pagamento Reembolsado', color: 'bg-purple-100 text-purple-700' },
    }

    const badge = actionMap[action] ?? { label: action, color: 'bg-gray-100 text-gray-700' }
    return (
      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${badge.color}`}>
        {badge.label}
      </span>
    )
  }

  const getTargetIcon = (targetType: string) => {
    const icons: Record<string, React.ReactNode> = {
      organization: <Building2 className="w-4 h-4 text-gray-400" />,
      user: <User className="w-4 h-4 text-gray-400" />,
      plan: <FileText className="w-4 h-4 text-gray-400" />,
      alert: <AlertTriangle className="w-4 h-4 text-gray-400" />,
      config: <Settings className="w-4 h-4 text-gray-400" />,
      subscription: <FileText className="w-4 h-4 text-gray-400" />,
      payment: <FileText className="w-4 h-4 text-gray-400" />,
    }
    return icons[targetType] ?? <FileText className="w-4 h-4 text-gray-400" />
  }

  if (loading) {
    return (
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <div className="p-8 text-center">
          <div className="animate-pulse flex flex-col items-center gap-4">
            <div className="w-12 h-12 bg-gray-200 rounded-full" />
            <div className="h-4 bg-gray-200 rounded w-48" />
          </div>
        </div>
      </div>
    )
  }

  if (logs.length === 0) {
    return (
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <div className="p-8 text-center">
          <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <FileText className="w-8 h-8 text-gray-400" />
          </div>
          <h3 className="font-medium text-gray-900 mb-2">Nenhum log encontrado</h3>
          <p className="text-sm text-gray-500">Tente ajustar os filtros de busca</p>
        </div>
      </div>
    )
  }

  return (
    <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-gray-50 border-b border-gray-200">
            <tr>
              <th className="text-left text-xs font-medium text-gray-500 uppercase px-6 py-3">Data/Hora</th>
              <th className="text-left text-xs font-medium text-gray-500 uppercase px-6 py-3">Ator</th>
              <th className="text-left text-xs font-medium text-gray-500 uppercase px-6 py-3">Ação</th>
              <th className="text-left text-xs font-medium text-gray-500 uppercase px-6 py-3">Alvo</th>
              <th className="text-left text-xs font-medium text-gray-500 uppercase px-6 py-3">IP</th>
              <th className="text-right text-xs font-medium text-gray-500 uppercase px-6 py-3">Detalhes</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {logs.map((log) => (
              <>
                <tr key={log.id} className="hover:bg-gray-50 transition-colors">
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-1.5 text-sm text-gray-600">
                      <Clock className="w-4 h-4 text-gray-400" />
                      {formatDateTime(log.created_at)}
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center">
                        <User className="w-4 h-4 text-gray-400" />
                      </div>
                      <span className="text-sm text-gray-700">{log.actor_email}</span>
                    </div>
                  </td>
                  <td className="px-6 py-4">{getActionBadge(log.action)}</td>
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-2">
                      {getTargetIcon(log.target_type)}
                      <div>
                        <p className="text-sm text-gray-700 capitalize">{log.target_type}</p>
                        {log.target_identifier && (
                          <p className="text-xs text-gray-500">{log.target_identifier}</p>
                        )}
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    {log.ip_address ? (
                      <div className="flex items-center gap-1.5 text-sm text-gray-600">
                        <Globe className="w-4 h-4 text-gray-400" />
                        {log.ip_address}
                      </div>
                    ) : (
                      <span className="text-sm text-gray-400">-</span>
                    )}
                  </td>
                  <td className="px-6 py-4 text-right">
                    <button
                      onClick={() => setExpandedLog(expandedLog === log.id ? null : log.id)}
                      className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                    >
                      <Eye className="w-4 h-4 text-gray-500" />
                    </button>
                  </td>
                </tr>
                {expandedLog === log.id && (
                  <tr key={`${log.id}-details`}>
                    <td colSpan={6} className="px-6 py-4 bg-gray-50">
                      <div className="grid grid-cols-2 gap-6">
                        {log.description && (
                          <div className="col-span-2">
                            <p className="text-xs text-gray-500 uppercase mb-1">Descrição</p>
                            <p className="text-sm text-gray-700">{log.description}</p>
                          </div>
                        )}
                        {log.old_values && Object.keys(log.old_values).length > 0 && (
                          <div>
                            <p className="text-xs text-gray-500 uppercase mb-1">Valores Anteriores</p>
                            <pre className="text-xs bg-white p-2 rounded border border-gray-200 overflow-x-auto">
                              {JSON.stringify(log.old_values, null, 2)}
                            </pre>
                          </div>
                        )}
                        {log.new_values && Object.keys(log.new_values).length > 0 && (
                          <div>
                            <p className="text-xs text-gray-500 uppercase mb-1">Valores Novos</p>
                            <pre className="text-xs bg-white p-2 rounded border border-gray-200 overflow-x-auto">
                              {JSON.stringify(log.new_values, null, 2)}
                            </pre>
                          </div>
                        )}
                        {log.user_agent && (
                          <div className="col-span-2">
                            <p className="text-xs text-gray-500 uppercase mb-1">User Agent</p>
                            <p className="text-xs text-gray-600 truncate">{log.user_agent}</p>
                          </div>
                        )}
                      </div>
                    </td>
                  </tr>
                )}
              </>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}
